import React, { useEffect, useState } from 'react';
import { X, Play, FileText, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * InstitutionalVideoModal — Phase B · Workstream 4
 * Displays a Founder-approved institutional video (or placeholder if not yet
 * generated). Reads `/api/institutional-videos/{id}`. Never autoplays audio.
 * Respects prefers-reduced-motion (fade-in only if user allows).
 */
export default function InstitutionalVideoModal({ videoId, isOpen, onClose, language = 'en' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);

  useEffect(() => {
    if (!isOpen || !videoId) return;
    setLoading(true);
    fetch(`${API_URL}/api/institutional-videos/${videoId}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [isOpen, videoId]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const title = language === 'es' ? data?.title_es : data?.title_en;
  const description = language === 'es' ? data?.description_es : data?.description_en;
  const script = language === 'es' ? data?.script_es : data?.script_en;
  const transcript = language === 'es' ? data?.transcript_es : data?.transcript_en;

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="institutional-video-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`ivm-title-${videoId}`}
    >
      <div
        className="bg-slate-900 border border-slate-800 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-4 md:p-6 border-b border-slate-800">
          <h2 id={`ivm-title-${videoId}`} className="text-xl md:text-2xl font-bold text-slate-100">
            {loading ? '…' : (title || 'Sleep Health Services')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 -m-2 rounded hover:bg-slate-800"
            aria-label="Close"
            data-testid="ivm-close-btn"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div className="p-4 md:p-6">
          {loading && <div className="text-slate-400">Loading…</div>}
          {!loading && data && (
            <>
              {data.video_url ? (
                <video
                  src={data.video_url}
                  poster={data.thumbnail_url || undefined}
                  controls
                  playsInline
                  className="w-full rounded-lg bg-black"
                  data-testid="ivm-video"
                >
                  {data.captions_url_en && (
                    <track kind="captions" srcLang="en" src={data.captions_url_en} label="English" default={language === 'en'} />
                  )}
                  {data.captions_url_es && (
                    <track kind="captions" srcLang="es" src={data.captions_url_es} label="Español" default={language === 'es'} />
                  )}
                </video>
              ) : (
                <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-6 text-amber-100" data-testid="ivm-placeholder">
                  <div className="flex items-center gap-2 mb-2 text-amber-300">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-semibold">
                      {language === 'es' ? 'Vídeo en preparación' : 'Video coming soon'}
                    </span>
                  </div>
                  <p className="text-sm">
                    {language === 'es'
                      ? 'Este vídeo institucional se producirá una vez que el fundador apruebe el guion final.'
                      : 'This institutional video will be produced once the Founder approves the final script.'}
                  </p>
                </div>
              )}

              <div className="mt-4 space-y-3">
                {description && (
                  <p className="text-slate-300 text-sm md:text-base leading-relaxed">{description}</p>
                )}
                {script && (
                  <div className="p-3 rounded bg-slate-800/50 border border-slate-700">
                    <div className="text-xs uppercase text-slate-500 mb-1">
                      {language === 'es' ? 'Guion aprobado' : 'Approved script'}
                    </div>
                    <p className="text-slate-200 text-sm leading-relaxed">{script}</p>
                  </div>
                )}
                {transcript && (
                  <div>
                    <button
                      onClick={() => setShowTranscript((v) => !v)}
                      className="flex items-center gap-2 text-sm text-cyan-400 hover:text-cyan-300"
                      data-testid="ivm-transcript-toggle"
                    >
                      <FileText className="w-4 h-4" />
                      {showTranscript
                        ? (language === 'es' ? 'Ocultar transcripción' : 'Hide transcript')
                        : (language === 'es' ? 'Ver transcripción' : 'View transcript')}
                    </button>
                    {showTranscript && (
                      <p className="mt-2 text-slate-300 text-sm whitespace-pre-line">{transcript}</p>
                    )}
                  </div>
                )}
                <div className="text-xs text-slate-500 pt-2 border-t border-slate-800">
                  {language === 'es' ? 'Estado: ' : 'Status: '}
                  <span className="text-slate-400">{data.approval_status}</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
